import { useLayoutEffect, useMemo } from 'react';
import { isEmpty, keyBy, mapValues, noop, identity, keys } from 'lodash';
import {
    COMMON_FLAGS,
    FEATURE_FLAGS,
    useCommonConfig,
    useConfigMatch,
} from 'modules/taboola-common-frontend-modules/account-configurations';
import { usePermissions } from 'modules/taboola-common-frontend-modules/authentication';
import { DEFAULT_TAGS, OTHER_TAGS_KEY } from './consts';

// order publishers for the tooltip
const SPECIAL_ORDER_PUBS = [OTHER_TAGS_KEY];
const SPECIAL_ORDER_PUBS_MAP = mapValues(keyBy(SPECIAL_ORDER_PUBS, identity), v => SPECIAL_ORDER_PUBS.indexOf(v) + 1);

const sort = (a, b) => {
    const aSpecialOrder = SPECIAL_ORDER_PUBS_MAP[a];
    const bSpecialOrder = SPECIAL_ORDER_PUBS_MAP[b];
    // if neither are special order, sort by locale
    if (!aSpecialOrder && !bSpecialOrder) {
        return a.localeCompare(b);
    }
    // if a is not a special order, it should be first
    if (!aSpecialOrder) {
        return -1;
    }
    // if b is not a special order, it should be first
    if (!bSpecialOrder) {
        return 1;
    }
    // if both are special order, sort by index
    return aSpecialOrder - bSpecialOrder;
};

export const useCRATags = ({ data, isRejected, setSkipRecommendation = noop }) => {
    const rawTags = data?.craTags || DEFAULT_TAGS;
    const isCRATagsEnabled = useConfigMatch({ [FEATURE_FLAGS.CREATIVE_REPORT_CRA_TAG_TOOLTIP_ENABLED]: 'true' });
    const { [COMMON_FLAGS.HIDDEN_CRA_TAGS_LIST]: rawHiddenCraTagsList } = useCommonConfig([
        COMMON_FLAGS.HIDDEN_CRA_TAGS_LIST,
    ]);
    const hiddenCraTagsSet = useMemo(() => {
        try {
            return new Set(
                rawHiddenCraTagsList
                    .split(',')
                    .filter(Boolean)
                    .map(v => parseInt(v.trim()))
                    .filter(v => !isNaN(v))
            );
        } catch (e) {
            return new Set();
        }
    }, [rawHiddenCraTagsList]);
    const isCRATagsPermitted = usePermissions('ADS_CONSOLE_CRA_TAG_TOOLTIP');

    // craTags is a map of publishers to tags
    // publishers is an array of publishers
    // initialSelected is the initial selected publisher
    const { craTags, publishers, initialSelected } = useMemo(() => {
        // transform the raw tags into a map of publishers to tags
        const craTags = rawTags
            .filter(({ value }) => !hiddenCraTagsSet.has(value.id))
            .reduce((res, { value, name }) => {
                if (!res[name]) {
                    res[name] = new Set();
                }
                res[name].add(value.tag);

                return res;
            }, {});

        // make array of the publishers and sort
        const publishers = keys(craTags).sort(sort);
        const initialSelected = publishers[0] || null;

        return { craTags, publishers, initialSelected };
    }, [hiddenCraTagsSet, rawTags]);

    const shouldRenderTooltip = useMemo(
        () => isCRATagsEnabled && isCRATagsPermitted && !isRejected && !isEmpty(publishers),
        [isCRATagsEnabled, isCRATagsPermitted, isRejected, publishers]
    );

    useLayoutEffect(() => {
        setSkipRecommendation(Boolean(shouldRenderTooltip));
    }, [setSkipRecommendation, shouldRenderTooltip]);

    return {
        shouldRenderTooltip,
        craTags,
        publishers,
        initialSelected,
    };
};
